import { Map, Record } from 'immutable';
import * as A from '@/domain/jeFieldRearrangement/actions';
import type {
  JeFieldRearrangementState,
  FieldRearrangementAction,
  SwapCellsPayload,
  CellFieldPayload,
  FieldRearrangementConfigStorage,
  AllConfigsStorage,
} from '@/domain/jeFieldRearrangement/types';

const EMPTY_CONFIG: FieldRearrangementConfigStorage = {
  positions: {},
  hidden: [],
  shown: [],
  locked: [],
};

export const JeFieldRearrangementFactory = Record<JeFieldRearrangementState>({
  configsByType: Map<string, FieldRearrangementConfigStorage>(),
});

type JeFieldRearrangementRecord = ReturnType<typeof JeFieldRearrangementFactory>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getConfig(state: JeFieldRearrangementRecord, type: string): FieldRearrangementConfigStorage {
  return state.configsByType.get(type) || EMPTY_CONFIG;
}

function setConfig(
  state: JeFieldRearrangementRecord,
  type: string,
  config: FieldRearrangementConfigStorage,
): JeFieldRearrangementRecord {
  return state.set('configsByType', state.configsByType.set(type, config));
}

/**
 * Serialize config for one transaction type (for API save).
 */
export function serializeConfig(
  state: JeFieldRearrangementRecord,
  transactionType: string,
): FieldRearrangementConfigStorage {
  return getConfig(state, transactionType);
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

const defaultState: JeFieldRearrangementRecord = JeFieldRearrangementFactory();

/**
 * Payload for actions that modify a specific transaction type's config.
 * The component wraps the original payload with `transactionType`.
 */
interface TypedPayload<T> {
  transactionType: string;
  inner: T;
}

export function jeFieldRearrangementReducer(
  state: JeFieldRearrangementRecord = defaultState,
  action: FieldRearrangementAction,
): JeFieldRearrangementRecord {
  switch (action.type) {
    case A.loadAllConfigsAction.type: {
      const allConfigs = action.payload as AllConfigsStorage;
      if (!allConfigs) return state.set('configsByType', Map<string, FieldRearrangementConfigStorage>());
      return state.set('configsByType', Map<string, FieldRearrangementConfigStorage>(Object.entries(allConfigs)));
    }

    case A.swapCellsAction.type: {
      const { transactionType, inner } = action.payload as TypedPayload<SwapCellsPayload>;
      const { fieldNameA, pairIdA, fieldNameB, pairIdB } = inner;
      if (!transactionType || !fieldNameA || !pairIdA || !pairIdB) return state;
      if (pairIdA === pairIdB) return state;

      const config = getConfig(state, transactionType);
      const positions = { ...config.positions, [fieldNameA]: pairIdB };
      if (fieldNameB) {
        positions[fieldNameB] = pairIdA;
      }
      return setConfig(state, transactionType, { ...config, positions });
    }

    case A.hideCellAction.type: {
      const { transactionType, inner } = action.payload as TypedPayload<CellFieldPayload>;
      const { fieldName } = inner;
      if (!transactionType || !fieldName) return state;
      const config = getConfig(state, transactionType);
      return setConfig(state, transactionType, {
        ...config,
        hidden: [...config.hidden.filter((f) => f !== fieldName), fieldName],
        shown: config.shown.filter((f) => f !== fieldName),
      });
    }

    case A.showCellAction.type: {
      const { transactionType, inner } = action.payload as TypedPayload<CellFieldPayload>;
      const { fieldName } = inner;
      if (!transactionType || !fieldName) return state;
      const config = getConfig(state, transactionType);
      return setConfig(state, transactionType, {
        ...config,
        shown: [...config.shown.filter((f) => f !== fieldName), fieldName],
        hidden: config.hidden.filter((f) => f !== fieldName),
      });
    }

    case A.lockCellAction.type: {
      const { transactionType, inner } = action.payload as TypedPayload<CellFieldPayload>;
      const { fieldName } = inner;
      if (!transactionType || !fieldName) return state;
      const config = getConfig(state, transactionType);
      if (config.locked.includes(fieldName)) return state;
      return setConfig(state, transactionType, {
        ...config,
        locked: [...config.locked, fieldName],
      });
    }

    case A.unlockCellAction.type: {
      const { transactionType, inner } = action.payload as TypedPayload<CellFieldPayload>;
      const { fieldName } = inner;
      if (!transactionType || !fieldName) return state;
      const config = getConfig(state, transactionType);
      return setConfig(state, transactionType, {
        ...config,
        locked: config.locked.filter((f) => f !== fieldName),
      });
    }

    case A.resetFieldRearrangementAction.type: {
      const transactionType = action.payload as string;
      if (!transactionType) return state;
      return setConfig(state, transactionType, { ...EMPTY_CONFIG });
    }

    default:
      return state;
  }
}

export const reducer = { jeFieldRearrangement: jeFieldRearrangementReducer };
